import React, { useState, useEffect } from 'react';
import { FiUpload, FiDownload, FiX, FiCheckCircle, FiAlertCircle, FiFile } from 'react-icons/fi';
import { importContractsFromExcel, downloadImportTemplate } from '../services/api';

const ImportModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  // Auto-hide success alert after 5 seconds
  useEffect(() => {
    if (showSuccessAlert) {
      const timer = setTimeout(() => {
        setShowSuccessAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessAlert]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      
      if (!validTypes.includes(selectedFile.type)) {
        setError('Please select a valid Excel file (.xlsx, .xls) or CSV file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setFile(selectedFile);
      setError('');
      setImportResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadImportTemplate();
    } catch (error) {
      setError('Failed to download template: ' + error.message);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setUploading(true);
    setError('');
    setImportResult(null);

    try {
      const result = await importContractsFromExcel(file, (progress) => {
        setUploadProgress(progress);
      });

      setImportResult(result);
      
      if (result.insertedCount > 0) {
        setShowSuccessAlert(true);
        if (onImportSuccess) {
          onImportSuccess();
        }
      }
    } catch (error) {
      setError('Import failed: ' + error.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploading(false);
    setUploadProgress(0);
    setImportResult(null);
    setError('');
    setShowSuccessAlert(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Success Alert Toast */}
      {showSuccessAlert && (
        <div className="fixed top-4 right-2 sm:right-4 z-[60] animate-slide-in max-w-[calc(100%-1rem)] sm:max-w-none">
          <div className="bg-green-500 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg shadow-lg flex items-center space-x-2 sm:space-x-3 min-w-[280px] sm:min-w-[300px]">
            <FiCheckCircle className="text-lg sm:text-xl flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm sm:text-base">Import Successful!</div>
              <div className="text-xs sm:text-sm opacity-90">
                {importResult?.insertedCount || 0} contracts imported successfully
              </div>
            </div>
            <button
              onClick={() => setShowSuccessAlert(false)}
              className="text-white hover:text-gray-200 transition-colors flex-shrink-0"
            >
              <FiX size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-lg p-0 w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden my-4 sm:my-0">
        {/* Modal Header */}
        <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg sm:text-2xl font-semibold text-gray-800 flex items-center">
            <FiUpload className="mr-2 text-blue-600" /> <span className="hidden sm:inline">Import Contracts</span><span className="sm:hidden">Import</span>
          </h2>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
            disabled={uploading}
          >
            <FiX size={24} className="sm:w-7 sm:h-7" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          {/* Instructions */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-base sm:text-lg font-semibold text-blue-800 mb-2">Import Instructions</h3>
            <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
              <li>• Download the template to see the required format</li>
              <li>• Fill in your contract data following the template structure</li>
              <li>• Required fields: name, vendor, end_date</li>
              <li>• Optional fields: description, vendor_contact, start_date, value, currency, status, alert_days, notification_email, region</li>
              <li>• If region is not specified, it will default to your assigned region</li>
              <li>• Supported formats: Excel (.xlsx, .xls) and CSV files</li>
            </ul>
          </div>

          {/* Date Formatting Warning */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-yellow-50 rounded-lg border border-yellow-300">
            <div className="flex items-start">
              <FiAlertCircle className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-semibold text-yellow-800 mb-2">⚠️ Important: Date Formatting</h3>
                <p className="text-xs sm:text-sm text-yellow-700 mb-2">
                  To ensure dates are imported correctly, <strong>format date columns as TEXT</strong> in Excel before entering dates:
                </p>
                <ol className="text-xs sm:text-sm text-yellow-700 list-decimal list-inside space-y-1 ml-2">
                  <li>Select the date columns (start_date, end_date)</li>
                  <li>Right-click → <strong>Format Cells</strong></li>
                  <li>Select <strong>"Text"</strong> category</li>
                  <li>Click <strong>OK</strong></li>
                  <li>Enter dates in <strong>DD/MM/YYYY</strong> format (e.g., 03/01/2026 for January 3rd, 2026)</li>
                </ol>
                <p className="text-xs text-yellow-600 mt-2 italic">
                  Note: If dates are already entered, you may need to re-enter them after changing the format to Text.
                </p>
              </div>
            </div>
          </div>

          {/* Template Download */}
          <div className="mb-4 sm:mb-6">
            <button
              onClick={handleDownloadTemplate}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm text-sm sm:text-base"
              disabled={uploading}
            >
              <FiDownload className="mr-2" />
              Download Template
            </button>
          </div>

          {/* File Upload */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                disabled={uploading}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FiFile className="text-3xl sm:text-4xl text-gray-400 mb-2" />
                <span className="text-xs sm:text-sm text-gray-600 break-words max-w-full">
                  {file ? file.name : 'Click to select file or drag and drop'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  Excel (.xlsx, .xls) or CSV files only
                </span>
              </label>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Uploading...</span>
                <span className="text-sm text-gray-500">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <FiAlertCircle className="text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center mb-3">
                <FiCheckCircle className="text-green-500 mr-2" />
                <span className="text-green-700 font-semibold">Import Completed</span>
              </div>
              <div className="text-sm text-green-700 space-y-1">
                <div>Total rows processed: {importResult.totalRows}</div>
                <div>Valid contracts: {importResult.processedContracts}</div>
                <div>Successfully imported: {importResult.insertedCount}</div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-3">
                    <div className="font-semibold">Errors:</div>
                    <ul className="list-disc list-inside space-y-1">
                      {importResult.errors.map((error, index) => (
                        <li key={index} className="text-red-600">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 sm:space-x-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base"
            disabled={uploading}
          >
            {importResult ? 'Close' : 'Cancel'}
          </button>
          {!importResult && (
            <button
              onClick={handleImport}
              disabled={!file || uploading}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {uploading ? 'Importing...' : 'Import Contracts'}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ImportModal;
